import * as THREE from "three/webgpu";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import type { GLTF } from "three/addons/loaders/GLTFLoader.js";
import type { BattleTeam } from "./BattleInteractionRouter";
import { createToonGradient } from "../toon";

const MATRIX = new THREE.Matrix4();
const POSITION = new THREE.Vector3();
const QUATERNION = new THREE.Quaternion();
const SCALE = new THREE.Vector3();
const EULER = new THREE.Euler();
const COLOR = new THREE.Color();
export const CAT_RUSH_DURATION = 2.8;

function hash(value: number): number {
  let result = value | 0;
  result = Math.imul(result ^ (result >>> 16), 0x21f0aaad);
  result = Math.imul(result ^ (result >>> 15), 0x735a2d97);
  return ((result ^ (result >>> 15)) >>> 0) / 0xffffffff;
}

/** CozyRoom's animated Mango cat, adapted into a full-lane physics charge. */
export class BattleCatRush {
  readonly root = new THREE.Group();
  readonly #cat = new THREE.Group();
  readonly #toonGradient = createToonGradient();
  readonly #speedLines: THREE.InstancedMesh<THREE.CylinderGeometry, THREE.MeshBasicMaterial>;
  readonly #dust: THREE.InstancedMesh<THREE.IcosahedronGeometry, THREE.MeshToonMaterial>;
  readonly #pawPads: THREE.InstancedMesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  readonly #pawToes: THREE.InstancedMesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>;
  readonly #impact: THREE.InstancedMesh<THREE.TetrahedronGeometry, THREE.MeshToonMaterial>;
  readonly #shockwaves: THREE.InstancedMesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  readonly #aura = new THREE.PointLight(0xffc96f, 0, 6, 2);
  #mixer: THREE.AnimationMixer | null = null;
  #model: THREE.Object3D | null = null;
  #warmFrames = 0;
  #attacker: BattleTeam = "ember";
  #startedAt = -10;

  constructor(scene: THREE.Scene) {
    this.root.name = "cozyroom-mango-cat-rush";
    this.root.add(this.#cat);
    scene.add(this.root);

    this.#speedLines = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.018, 0.045, 1, 5),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.72, depthWrite: false, blending: THREE.AdditiveBlending, vertexColors: true }),
      84,
    );
    this.#speedLines.name = "cat-rush-instanced-speed-lines";
    this.#speedLines.frustumCulled = false;
    this.#speedLines.renderOrder = 9;
    this.root.add(this.#speedLines);

    this.#dust = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(0.09, 1),
      new THREE.MeshToonMaterial({ color: 0xf2dfbd, transparent: true, opacity: 0.88 }),
      72,
    );
    this.#dust.name = "cat-rush-instanced-dust";
    this.#dust.frustumCulled = false;
    this.root.add(this.#dust);

    this.#pawPads = new THREE.InstancedMesh(
      new THREE.RingGeometry(0.12, 0.24, 14),
      new THREE.MeshBasicMaterial({ color: 0xffdc73, transparent: true, opacity: 0.72, depthWrite: false, blending: THREE.AdditiveBlending }),
      14,
    );
    this.#pawPads.name = "cat-rush-magic-paw-pads";
    this.#pawPads.frustumCulled = false;
    this.root.add(this.#pawPads);

    this.#pawToes = new THREE.InstancedMesh(
      new THREE.CircleGeometry(0.065, 10),
      new THREE.MeshBasicMaterial({ color: 0xffefb0, transparent: true, opacity: 0.78, depthWrite: false, blending: THREE.AdditiveBlending }),
      42,
    );
    this.#pawToes.name = "cat-rush-magic-paw-toes";
    this.#pawToes.frustumCulled = false;
    this.root.add(this.#pawToes);

    this.#impact = new THREE.InstancedMesh(
      new THREE.TetrahedronGeometry(0.11, 0),
      new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }),
      96,
    );
    this.#impact.name = "cat-rush-ballistic-impact";
    this.#impact.frustumCulled = false;
    for (let index = 0; index < 96; index += 1) {
      this.#impact.setColorAt(index, COLOR.set(index % 3 === 0 ? 0xffd46c : index % 2 ? 0x71d9d0 : 0xff826f));
    }
    this.root.add(this.#impact);

    this.#shockwaves = new THREE.InstancedMesh(
      new THREE.TorusGeometry(1, 0.045, 5, 48),
      new THREE.MeshBasicMaterial({ color: 0xffefad, transparent: true, opacity: 0.72, depthWrite: false, blending: THREE.AdditiveBlending }),
      5,
    );
    this.#shockwaves.name = "cat-rush-shockwaves";
    this.#shockwaves.frustumCulled = false;
    this.#shockwaves.renderOrder = 10;
    this.root.add(this.#shockwaves, this.#aura);
    this.root.visible = false;
  }

  async load(): Promise<void> {
    const loader = new GLTFLoader();
    const textureLoader = new THREE.TextureLoader();
    const [gltf, texture] = await Promise.all([
      loader.loadAsync("/assets/cozycat/cat.glb") as Promise<GLTF>,
      textureLoader.loadAsync("/assets/cozycat/cat_orange.png"),
    ]);
    texture.flipY = false;
    texture.colorSpace = THREE.SRGBColorSpace;
    const model = gltf.scene;
    model.name = "CozyRoom-Mango";
    model.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const previous = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of previous) material?.dispose();
      mesh.material = new THREE.MeshToonMaterial({ map: texture, gradientMap: this.#toonGradient });
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
    });
    this.#cat.add(model);
    model.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(model);
    const height = Math.max(0.01, bounds.max.y - bounds.min.y);
    model.position.y -= bounds.min.y;
    this.#cat.scale.setScalar(2.25 / height);
    this.#model = model;
    this.#mixer = new THREE.AnimationMixer(model);
    const run = THREE.AnimationClip.findByName(gltf.animations, "Run") ?? gltf.animations[0];
    if (run) this.#mixer.clipAction(run).play();
    this.#warmFrames = 3;
  }

  trigger(attacker: BattleTeam, time: number): void {
    this.#attacker = attacker;
    this.#startedAt = time;
    this.root.position.z = attacker === "ember" ? -1.35 : 1.35;
  }

  update(dt: number, time: number, frontline: number, reducedMotion: boolean): void {
    const age = time - this.#startedAt;
    const active = age >= 0 && age < CAT_RUSH_DURATION;
    if (!active || !this.#model) {
      this.root.visible = this.#model !== null && this.#warmFrames > 0;
      if (this.root.visible) {
        this.#cat.position.set(0, -100, 0);
        for (const mesh of [this.#speedLines, this.#dust, this.#pawPads, this.#pawToes, this.#impact, this.#shockwaves]) mesh.count = 0;
        this.#warmFrames -= 1;
      }
      return;
    }
    this.root.visible = true;
    this.#mixer?.update(Math.min(dt, 0.06) * 1.75);

    const direction = this.#attacker === "ember" ? 1 : -1;
    const progress = THREE.MathUtils.clamp(age / CAT_RUSH_DURATION, 0, 1);
    const travel = THREE.MathUtils.smootherstep(progress, 0.03, 0.88);
    const catX = THREE.MathUtils.lerp(-direction * 5.35, direction * 5.1, travel);
    const fade = 1 - THREE.MathUtils.smoothstep(progress, 0.9, 1);
    const speedPulse = Math.sin(progress * Math.PI);
    this.#cat.position.set(catX, 0.02 + Math.abs(Math.sin(time * 12)) * 0.07, Math.sin(time * 4.2) * 0.12);
    this.#cat.rotation.y = direction > 0 ? Math.PI / 2 : -Math.PI / 2;
    this.#cat.rotation.z = -direction * speedPulse * 0.08;
    this.#aura.position.set(catX, 0.8, 0);
    this.#aura.color.setHex(this.#attacker === "ember" ? 0xff8a67 : 0x76eff0);
    this.#aura.intensity = 3.2 * speedPulse * fade;

    this.#updateSpeedLines(time, catX, direction, speedPulse, fade, reducedMotion);
    this.#updateDust(time, catX, direction, fade, reducedMotion);
    this.#updatePaws(frontline, direction, progress, fade, reducedMotion);
    this.#updateImpact(age, direction, fade, reducedMotion);
  }

  dispose(): void {
    this.#mixer?.stopAllAction();
    if (this.#model) {
      this.#model.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (!mesh.isMesh) return;
        mesh.geometry?.dispose();
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const material of materials) material.dispose();
      });
    }
    for (const mesh of [this.#speedLines, this.#dust, this.#pawPads, this.#pawToes, this.#impact, this.#shockwaves]) {
      mesh.geometry.dispose();
      mesh.material.dispose();
    }
    this.#toonGradient.dispose();
    this.root.removeFromParent();
  }

  #updateSpeedLines(time: number, catX: number, direction: number, strength: number, fade: number, reducedMotion: boolean): void {
    const count = reducedMotion ? 28 : 84;
    this.#speedLines.count = count;
    for (let index = 0; index < count; index += 1) {
      const seed = hash(index * 71 + 5);
      const length = 0.5 + seed * 2.1;
      POSITION.set(
        catX - direction * (0.6 + hash(index * 37 + 11) * 3.2),
        0.14 + hash(index * 43 + 17) * 1.55,
        -1.55 + hash(index * 61 + 23) * 3.1,
      );
      EULER.set(0, 0, Math.PI / 2 + Math.sin(time * 4 + seed * 8) * 0.035);
      QUATERNION.setFromEuler(EULER);
      SCALE.set(0.55 + strength, length * (0.35 + strength), 0.55 + strength);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#speedLines.setMatrixAt(index, MATRIX);
      this.#speedLines.setColorAt(index, COLOR.set(index % 3 === 0 ? 0xffe289 : this.#attacker === "ember" ? 0xff7866 : 0x64e5df));
    }
    this.#speedLines.instanceMatrix.needsUpdate = true;
    if (this.#speedLines.instanceColor) this.#speedLines.instanceColor.needsUpdate = true;
    this.#speedLines.material.opacity = 0.72 * strength * fade;
  }

  #updateDust(time: number, catX: number, direction: number, fade: number, reducedMotion: boolean): void {
    const count = reducedMotion ? 24 : 72;
    this.#dust.count = count;
    for (let index = 0; index < count; index += 1) {
      const seed = hash(index * 79 + 29);
      const particleAge = (time * (0.62 + seed * 0.25) + seed * 2.7) % 1;
      POSITION.set(
        catX - direction * (0.35 + particleAge * (1.2 + seed * 1.4)),
        0.05 + Math.sin(particleAge * Math.PI) * (0.22 + seed * 0.52),
        (hash(index * 53 + 3) - 0.5) * 1.45,
      );
      QUATERNION.identity();
      const size = (0.45 + seed * 1.5) * fade * (1 - particleAge * 0.55);
      SCALE.set(size * 1.5, size * 0.65, size);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#dust.setMatrixAt(index, MATRIX);
    }
    this.#dust.instanceMatrix.needsUpdate = true;
    this.#dust.material.opacity = 0.86 * fade;
  }

  #updatePaws(frontline: number, direction: number, progress: number, fade: number, reducedMotion: boolean): void {
    const maxPrints = reducedMotion ? 7 : 14;
    let padCount = 0;
    let toeCount = 0;
    for (let index = 0; index < maxPrints; index += 1) {
      const pathProgress = (index + 1) / 15;
      if (pathProgress > progress * 1.12) break;
      const x = THREE.MathUtils.lerp(-direction * 5.15, direction * 4.72, pathProgress);
      const z = (index % 2 === 0 ? -1 : 1) * (0.35 + hash(index * 13) * 0.18);
      POSITION.set(x, 0.09, z);
      EULER.set(-Math.PI / 2, 0, direction * Math.PI / 2);
      QUATERNION.setFromEuler(EULER);
      SCALE.setScalar(0.8 + hash(index * 31) * 0.35);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#pawPads.setMatrixAt(padCount, MATRIX);
      padCount += 1;
      for (let toe = 0; toe < 3; toe += 1) {
        POSITION.set(x + direction * (0.18 + (toe === 1 ? 0.07 : 0)), 0.095, z + (toe - 1) * 0.14);
        MATRIX.compose(POSITION, QUATERNION, SCALE.setScalar(0.7));
        this.#pawToes.setMatrixAt(toeCount, MATRIX);
        toeCount += 1;
      }
    }
    this.#pawPads.count = padCount;
    this.#pawToes.count = toeCount;
    this.#pawPads.instanceMatrix.needsUpdate = true;
    this.#pawToes.instanceMatrix.needsUpdate = true;
    this.#pawPads.material.opacity = 0.7 * fade;
    this.#pawToes.material.opacity = 0.76 * fade;
    this.#pawPads.position.x = frontline * 0.06;
    this.#pawToes.position.x = frontline * 0.06;
  }

  #updateImpact(age: number, direction: number, fade: number, reducedMotion: boolean): void {
    const impactAge = age - CAT_RUSH_DURATION * 0.72;
    const active = impactAge >= 0;
    const count = active ? (reducedMotion ? 32 : 96) : 0;
    this.#impact.count = count;
    for (let index = 0; index < count; index += 1) {
      const seed = hash(index * 89 + 31);
      const t = Math.min(1.1, impactAge + hash(index * 41 + 9) * 0.08);
      const angle = hash(index * 67 + 7) * Math.PI * 2;
      const speed = 1.8 + seed * 5.2;
      POSITION.set(
        direction * 4.45 + Math.cos(angle) * speed * t,
        0.18 + Math.max(0, (3 + seed * 4.2) * t - 5.8 * t * t),
        Math.sin(angle) * speed * t,
      );
      EULER.set(t * (2 + seed * 8), angle, t * 4.2);
      QUATERNION.setFromEuler(EULER);
      const size = (0.6 + seed * 2.1) * fade;
      SCALE.set(size, size * 1.5, size);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#impact.setMatrixAt(index, MATRIX);
    }
    this.#impact.instanceMatrix.needsUpdate = true;

    for (let index = 0; index < this.#shockwaves.count; index += 1) {
      const ringAge = Math.max(0, impactAge - index * 0.07);
      const ringProgress = THREE.MathUtils.clamp(ringAge / 0.72, 0, 1);
      POSITION.set(direction * 4.45, 0.14 + index * 0.04, 0);
      EULER.set(Math.PI / 2, 0, 0);
      QUATERNION.setFromEuler(EULER);
      SCALE.setScalar(active ? (0.2 + ringProgress * 2.65) * fade : 0);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#shockwaves.setMatrixAt(index, MATRIX);
    }
    this.#shockwaves.instanceMatrix.needsUpdate = true;
    this.#shockwaves.material.opacity = active ? (1 - Math.min(1, impactAge / 0.72)) * 0.74 : 0;
  }
}
